// src/features/chat/components/his/ReceiptCard.jsx
/*
 * A receipt is the PA saying "filed." — a glanceable trace plus an undo
 * handle, not an audit record.
 *
 * Fresh (witnessed): a small card with Open Record and Undo. Settled: a
 * one-line whisper that re-expands on tap; Undo appears while the server
 * still allows it, otherwise a conversational seed. Undone: the quietest
 * trace of all. Settling is display only — undo availability is server truth.
 *
 * Card and whisper phases stay mounted inside Collapse so settling is a
 * collapse + de-materialization in place, and keyboard focus is restored
 * across user-initiated expand/collapse.
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronUp, FileText, RotateCcw } from 'lucide-react';
import styles from '../../styles/HisArtifact.module.css';
import Collapse from './Collapse';
import ArtifactMetaLine from './ArtifactMetaLine';
import {
  canUndoReceipt,
  receiptDisplayState,
} from '../../models/artifactLifecycleModel';
import {
  receiptFields,
  receiptRecords,
  receiptTitle,
  receiptWhisperText,
  undoLapsedSeed,
  undoneWhisperText,
} from '../../templates/receiptTemplates';

export function ReceiptCard({
  receipt,
  fresh = false,
  expanded = false,
  born = false,
  undoBusy = false,
  disabled = false,
  notice = null,
  showMeta = true,
  onToggle,
  onUndo,
  onSeedComposer,
  botName,
  timeMs = 0,
}) {
  const state = receiptDisplayState(receipt);
  const showCard = state === 'committed' && (fresh || expanded);
  const canUndo = canUndoReceipt(receipt) && typeof onUndo === 'function';
  const records = receiptRecords(receipt);
  const fields = receiptFields(receipt);
  const seed = undoLapsedSeed();

  const whisperRef = useRef(null);
  const collapseRef = useRef(null);
  const pendingFocusRef = useRef(null);

  // Restore keyboard focus across user-initiated expand/collapse only;
  // passive settling (frontier movement, backstop) never steals focus.
  useEffect(() => {
    if (pendingFocusRef.current === 'card') collapseRef.current?.focus();
    if (pendingFocusRef.current === 'whisper') whisperRef.current?.focus();
    pendingFocusRef.current = null;
  }, [showCard]);

  const handleExpand = () => {
    pendingFocusRef.current = 'card';
    onToggle?.(receipt.id);
  };

  const handleCollapse = () => {
    pendingFocusRef.current = 'whisper';
    onToggle?.(receipt.id);
  };

  const renderSettled = () => {
    if (state === 'undone') {
      return (
        <div className={styles.whisper} data-quiet="true">
          <span className={styles.whisperGlyphMuted} aria-hidden="true">
            <RotateCcw size={13} />
          </span>
          <span>{undoneWhisperText(receipt)}</span>
        </div>
      );
    }
    return (
      <div
        ref={whisperRef}
        className={styles.whisper}
        data-interactive="true"
        role="button"
        tabIndex={0}
        aria-expanded={false}
        onClick={handleExpand}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleExpand();
          }
        }}
      >
        <span className={styles.whisperGlyphOk} aria-hidden="true">
          <Check size={13} strokeWidth={2.4} />
        </span>
        <span>{receiptWhisperText(receipt)}</span>
      </div>
    );
  };

  return (
    <div
      className={[styles.card, born ? styles.rise : ''].filter(Boolean).join(' ')}
      data-kind="receipt"
      data-settled={showCard ? 'false' : 'true'}
    >
      <Collapse open={showCard}>
        <div>
          {showMeta && <ArtifactMetaLine botName={botName} timeMs={timeMs} />}
          <div className={styles.header}>
            <span className={styles.headerIcon}>
              <FileText size={15} aria-hidden="true" />
            </span>
            <span className={styles.title}>{receiptTitle(receipt)}</span>
            {!fresh && (
              <button
                ref={collapseRef}
                type="button"
                className={styles.detailsButton}
                data-flush="true"
                onClick={handleCollapse}
                aria-label="Collapse"
                aria-expanded
              >
                <ChevronUp size={13} aria-hidden="true" />
              </button>
            )}
          </div>

          {fields.length > 0 && (
            <dl className={styles.fieldList}>
              {fields.map((field, index) => (
                <div key={`${field.key}:${index}`} className={styles.fieldRow}>
                  <dt>{field.label}</dt>
                  <dd>{field.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {notice && <p className={styles.inlineNotice}>{notice}</p>}

          <div className={styles.linkRow}>
            {records.map((record) => (
              <Link
                key={record.id}
                to={`/record/${record.id}`}
                className={styles.recordLink}
              >
                <FileText size={13} aria-hidden="true" />
                <span>Open Record</span>
              </Link>
            ))}
            {canUndo ? (
              <button
                type="button"
                className={styles.textAction}
                onClick={() => onUndo(receipt.id)}
                disabled={disabled || undoBusy}
              >
                {undoBusy && <span className={styles.spinner} aria-hidden="true" />}
                Undo
              </button>
            ) : (
              typeof onSeedComposer === 'function' && (
                <button
                  type="button"
                  className={styles.textAction}
                  onClick={() => onSeedComposer(seed.text)}
                >
                  {seed.label}
                </button>
              )
            )}
          </div>
        </div>
      </Collapse>

      <Collapse open={!showCard}>{renderSettled()}</Collapse>
    </div>
  );
}

export default ReceiptCard;
